import React, { useMemo } from 'react'
import { BarChart3, Loader2 } from 'lucide-react'
import { ThemePreset } from '../../theme/presets'

interface BarData {
  label: string
  value: number
}

interface BarChartProps {
  data: BarData[]
  loading: boolean
  palette: ThemePreset
}

const fmtMoney = (value: number): string =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)

export const BarChart: React.FC<BarChartProps> = ({ data, loading, palette }) => {
  const maxValue = useMemo(() => Math.max(...data.map((d) => d.value), 1), [data])

  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm p-5">
      <div className="flex items-center gap-2 mb-4">
        <BarChart3 className="w-5 h-5" style={{ color: palette.primary }} />
        <h3 className="text-base font-semibold text-slate-800 dark:text-white">
          Ranking de Vendedores
        </h3>
        {!loading && data.length > 0 && (
          <span className="text-xs text-slate-400 dark:text-slate-500 ml-auto">
            Top {data.length}
          </span>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-[300px]">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : !data.length ? (
        <div className="flex items-center justify-center h-[300px] text-slate-400 text-sm">
          Nenhum dado de vendedor no período selecionado.
        </div>
      ) : (
        <div className="space-y-2.5 overflow-y-auto max-h-[650px] pr-1 scrollbar-standard">
          {data.map((item) => {
            const pct = (item.value / maxValue) * 100
            return (
              <div key={item.label} className="group">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300 truncate max-w-[120px]">
                    {item.label}
                  </span>
                  <span className="text-sm font-semibold text-slate-800 dark:text-white tabular-nums">
                    {fmtMoney(item.value)}
                  </span>
                </div>
                <div className="h-3 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500 ease-out"
                    style={{
                      width: `${pct}%`,
                      background: `linear-gradient(90deg, ${palette.primary}, ${palette.primary}cc)`,
                    }}
                  />
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
